// shared-lib/cabinet-runs/vanityBasinSeat.js
//
// A vanity's basin hangs from its COUNTER, so when the run rebuilds the
// counter the basin has to be FITTED to it — seated vertically, and where the
// bowl does not fit the deck at all, redrawn at a size that does.
//
// The basin is an incumbent: the run reserves its footprint as an appliance
// bay and never re-emits it, which is what keeps the catalog bowl the scan
// matched. `moveApplianceObjectsToBays` then moves it ALONG the wall and
// ACROSS it — both horizontal. Nothing moved it vertically, so a re-solve that
// changed the body height left the bowl behind: measured on `D6B52714`, a
// converted 30 ¼" vanity rebuilt at the profile's 32" put the counter 1 ¾"
// higher and the scanned undermount's rim that far BELOW the underside of the
// slab — a bowl hanging in mid-air under a counter it no longer touches.
//
// The seat itself is NOT decided here. `calculateSinkSeatY` already owns it
// for creation and for the swap path, mount by mount (an undermount's box top
// flush with the slab's underside, a drop-in one thickness higher, a vessel
// resting on top), and it was extracted precisely so those two could not
// drift. This is a third caller, not a third copy.
//
// Pure. No I/O.

const { calculateSinkSeatY } = require('../vanity-assembly/calculateSinkSeatY');
const {
  PROCEDURAL_BASIN_MODEL_ID,
  PROCEDURAL_BASIN_SINK_TYPE,
  proceduralBasinDimensions,
} = require('../vanity-assembly/proceduralBasin');
const { buildSinkDimensions } = require('../sink-types');
const {
  clearGlbModelFields,
} = require('../fixture-types/productRepresentation');

/** What the panel should call the bowl once it is ours and not a product. */
const PROCEDURAL_BASIN_NAME = 'Undermount Basin';
const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { profileOf, VANITY_PROFILE_ID } = require('./profiles');
const { baysForRun, isApplianceKind } = require('./bays');
const { bowlsOfSinkBay } = require('./vanitySinkBay');

/** A millimetre: below this the move is noise, not a seat. */
const SEAT_EPS = 1e-3;

/** The run's counter nearest this point in plan — a vanity has one. */
function nearestCounter(counters, point) {
  let best = null;
  let bestD = Infinity;
  for (const c of counters) {
    if (!c?.center || !c.dimensions) continue;
    const d = Math.hypot(c.center.x - point.x, c.center.z - point.z);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

/**
 * Does this bowl fit the deck it is set into, front to back?
 *
 * Measured on the corpus: 6 of 56 scanned vanity basins are deeper than their
 * own counter allows, and every one of them is the same product —
 * `kohler-2330-g`, 15.61" deep — sitting in counters from 11.6" to 17.4". The
 * scan recorded it that way; the run reproduces it faithfully and the bowl
 * hangs through the front of the cabinetry, which is what a user sees.
 *
 * The test is whether the bowl fits the counter's FOOTPRINT, not whether it
 * keeps the standard deck margin. Three of those six do fit with a thin deck
 * (0.15"-0.9" a side) and look perfectly ordinary; redrawing those would be
 * over-eager. The three that do not — an 11.6" counter and a pair of 14.7"
 * ones against a 15.6" bowl — are the ones that protrude.
 */
function bowlFitsDeck(basin, counterDepth) {
  const bowl = basin?.dimensions?.z;
  if (!(bowl > 0) || !(counterDepth > 0)) return true; // nothing to judge
  return bowl <= counterDepth + 1e-3;
}

/**
 * The bowl we DRAW, fitted to this counter — for a basin whose product cannot
 * sit in the deck at all.
 *
 * Sized by `proceduralBasinDimensions`, the one rule the builder and the
 * model picker share, and stamped with the procedural model id so the panel
 * reports the built-in render rather than calling a drawn bowl a catalog
 * undermount. The size it returns already carries the deck margins, so
 * CENTRING it in the counter's own footprint leaves them equal front and
 * back — which is also what stops it reaching into the wall, as the product
 * it replaces did by 0.63".
 */
function refittedBasin(basin, { counter, bayWidth, axes, now }) {
  const counterDepth = counter.dimensions.z;
  const drawn = proceduralBasinDimensions({
    width: bayWidth,
    depth: counterDepth,
  });
  if (!(drawn.width > 0) || !(drawn.depth > 0)) return null;
  // How far a point stands off the wall FACE, along its inward normal — the
  // frame the deck's own depth is measured in.
  const offNormal = (p) =>
    (p.x - axes.start.x) * axes.wallNormal.x +
    (p.z - axes.start.z) * axes.wallNormal.z;
  // Slide it along the NORMAL to the middle of the deck, keeping its place
  // along the wall: where it sits along the run is the bay's business
  // (`vanitySinkBay.js`) and was decided before this.
  const deckBack = offNormal(counter.center) - counterDepth / 2;
  const target = deckBack + counterDepth / 2;
  const shift = target - offNormal(basin.center);
  // DETACH THE PRODUCT, the way the model picker does when someone chooses
  // the placeholder tile (`computeModelUpdate`: `selectedModelType:
  // 'procedural'` with `modelUrl`/`scaleFactor` nulled).
  //
  // Stamping `modelId: 'procedural'` alone is NOT enough and that is what
  // shipped first: `objectIsExplicitOptOut` recognises `isProcedural`,
  // `selectedModelType` and `modelId === 'none'` — not `modelId ===
  // 'procedural'` — so with the catalog `modelUrl` still attached the object
  // still SHOWED a GLB, the renderer went on drawing the Kohler mesh at its
  // own 15.61" depth, and the refit existed only in the data. The same
  // predicate is why the dimensions had to be written through
  // `buildSinkDimensions`: the nested `width`/`depth`/`height` still
  // described the product, and `mayResizeObject` says a shown product's size
  // is the product's.
  return {
    // Every GLB-only field goes, not just the url. `sink-cutout` is the one
    // that shows: it is the PRODUCT'S RIM POLYGON and `resolveSinkCutoutSpec`
    // cuts the deck to it ahead of anything else, so leaving it behind put the
    // Kohler's rectangular opening around the ellipsoid the placeholder
    // renderer draws — an oval bowl in a square hole, which is what came back
    // from the first attempt. Cleared, that function cuts the ellipse the
    // renderer draws. `faucet-mounting-points` would likewise go on seating a
    // faucet off a model that is gone.
    ...clearGlbModelFields(basin),
    modelId: PROCEDURAL_BASIN_MODEL_ID,
    selectedModelType: 'procedural',
    modelName: PROCEDURAL_BASIN_NAME,
    sinkType: PROCEDURAL_BASIN_SINK_TYPE,
    dimensions: buildSinkDimensions({
      width: drawn.width,
      depth: drawn.depth,
      height: drawn.height,
      // The hole through the counter follows the nominal bowl, as the
      // builder's does.
      cutoutDepth: drawn.height,
    }),
    center: {
      ...basin.center,
      x: basin.center.x + shift * axes.wallNormal.x,
      z: basin.center.z + shift * axes.wallNormal.z,
    },
    lastModified: now,
  };
}

/**
 * Every bowl a vanity run's bays hold, by id, with the bay holding it — the
 * line's double-bowl unit holds TWO, one of them with no bay of its own
 * (`bowlsOfSinkBay`). Seating only the bay's own left the second bowl at the
 * old counter after every re-solve. Shared with `vanityCounterFollow.js`, so
 * the bowls that are seated and the bowls whose fittings follow are one set.
 */
function basinBaysFor(run, objects) {
  const bayFor = new Map();
  for (const b of baysForRun(run)) {
    if (!isApplianceKind(b.kind) || !b.applianceId) continue;
    bayFor.set(b.applianceId, b);
    for (const bowl of bowlsOfSinkBay(b, objects)) {
      if (!bayFor.has(bowl.id)) bayFor.set(bowl.id, b);
    }
  }
  return bayFor;
}

/**
 * Fit every basin this VANITY run hosts to the counter it just built.
 *
 * Vanity runs only: a kitchen's scanned base is re-solved at the standard
 * 34.5" by design (`minimal-change.md`) and its basin seat is owned by
 * `buildKitchenSinkBasin` off the real slab (#2242). The same latent gap
 * exists there and is deliberately not widened from here.
 *
 * @param {Object[]} objects the merged room objects (counters already emitted)
 * @param {Object} run       the rehydrated run
 * @param {Object} [ctx]     `{ now, step }`, as the mover takes
 * @returns {{objects: Object[], moved: string[]}} the input array by IDENTITY
 *   when nothing moved, so a caller can skip a write.
 */
function fitVanityBasinsToCounter(objects, run, ctx = {}) {
  const list = Array.isArray(objects) ? objects : [];
  if (profileOf(run).id !== VANITY_PROFILE_ID)
    return { objects: list, moved: [] };
  const bayFor = basinBaysFor(run, list);
  const hosted = new Set(bayFor.keys());
  if (!hosted.size) return { objects: list, moved: [] };
  const counters = list.filter(
    (o) => o?.type === 'countertop' && o.runId === run.id
  );
  if (!counters.length) return { objects: list, moved: [] };

  const axesFor = (bay) => {
    const seg = (run.segments || [])[bay?.segmentIndex ?? 0];
    return seg?.wall ? getWallAxes(seg.wall) : null;
  };
  const now = ctx.now || new Date().toISOString();

  const moved = [];
  const next = list.map((raw) => {
    if (!raw?.id || !hosted.has(raw.id) || raw.type !== 'sink') return raw;
    const counter = nearestCounter(counters, raw.center || {});
    if (!counter) return raw;
    // FIRST, does the product even fit the deck? Where it cannot, the bowl is
    // redrawn at a size that does, and the seat below then applies to that.
    const bay = bayFor.get(raw.id);
    const axes = axesFor(bay);
    let o = raw;
    if (axes && bay && !bowlFitsDeck(raw, counter.dimensions?.z)) {
      const refit = refittedBasin(raw, {
        counter,
        bayWidth: bay.width,
        axes,
        now,
      });
      if (refit) o = refit;
    }
    const height = o.dimensions?.y;
    if (!(height > 0)) return raw;
    const top = counter.center.y + counter.dimensions.y / 2;
    const bottom = counter.center.y - counter.dimensions.y / 2;
    const seatY = calculateSinkSeatY({
      // What the basin says it is. An unrecognised or absent mount is left
      // ALONE rather than guessed at: a wall-hung basin is not counter-hosted,
      // and inventing a seat for one would drop it through the floor.
      sinkType: o.sinkType,
      modelHeightMeters: height,
      countertopTopY: top,
      countertopBottomY: bottom,
      // Where a vessel's contact ring sits up its box, as creation and the
      // swap path read it (`assembleVanity`, `swapSinkModel`): the model's
      // measured `vessel-bottom`, which the basin carries. This read
      // `o.vesselRingOffsetInches`, a field no basin has, so every vessel a
      // vanity run re-seated was rested on its box bottom instead — 3.5"
      // above the builder's seat for the reported room's bowl.
      vesselRingOffsetInches: o['vessel-bottom']?.yOffsetFromBottomInches,
    });
    const seated = seatY != null && Math.abs(seatY - o.center.y) > SEAT_EPS;
    if (!seated && o === raw) return raw;
    moved.push(o.id);
    const dy = seated ? seatY - o.center.y : 0;
    const out = {
      ...o,
      ...(seated ? { center: { ...o.center, y: seatY } } : {}),
      lastModified: now,
    };
    if (run.source === 'scan') {
      out.scanAdjusted = [
        ...(Array.isArray(o.scanAdjusted) ? o.scanAdjusted : []),
        { step: ctx.step || 'run-bay', kind: 'reseated', deltaY: dy },
      ];
    }
    return out;
  });
  return moved.length ? { objects: next, moved } : { objects: list, moved: [] };
}

module.exports = {
  fitVanityBasinsToCounter,
  basinBaysFor,
  nearestCounter,
  bowlFitsDeck,
  SEAT_EPS,
};
